/**
 * LanguageSelector Component
 *
 * Dropdown for switching the application language. Lists every locale from
 * `@tonnie/locale` (`SUPPORTED_LOCALES` / `LOCALE_NAMES`) and drives the change
 * through `useLocale().setLocale`, which persists to localStorage (and the
 * backend `users.locale` post-auth) — same source of truth as the Settings
 * picker. Languages are shown in their native names (standard for a picker).
 *
 * Two trigger styles:
 *   • default (icon-only) — a ghost globe button; used in the app-shell header
 *     where space is tight and the surrounding chrome gives context.
 *   • `showLabel` — globe + the current language's native name + chevron; used on
 *     the pre-auth pages (login/signup), where this is the ONLY affordance to
 *     pick a language, so it must be self-evident.
 *
 * Theme-compliant: Uses CSS variables for colors.
 */

import { Check, ChevronDown, Globe } from '@tonnie/assets';
import { cn } from '@tonnie/foundation';
import { useLocale, useTranslation, SUPPORTED_LOCALES, LOCALE_NAMES } from '@tonnie/locale';
import {
  Button,
  CountryFlag,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonnie/ui';

import { LOCALE_FLAG_COUNTRIES } from '@/core/config/locale-flags';

export interface ILanguageSelectorProps {
  /** Additional CSS classes */
  className?: string;
  /** Show the current language name + chevron next to the globe (default: icon-only). */
  showLabel?: boolean;
}

export function LanguageSelector({ className, showLabel = false }: ILanguageSelectorProps) {
  const { locale, setLocale } = useLocale();
  const { t } = useTranslation();
  const ariaLabel = t('common.selectLanguage');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {showLabel ? (
          <Button
            variant="ghost"
            size="sm"
            className={cn('gap-2', className)}
            aria-label={ariaLabel}
          >
            <Globe className="w-4 h-4" />
            <span className="text-sm font-medium">{LOCALE_NAMES[locale]?.native ?? ariaLabel}</span>
            <ChevronDown className="w-4 h-4 opacity-60" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className={className} aria-label={ariaLabel}>
            <Globe className="w-5 h-5" />
            <span className="sr-only">{LOCALE_NAMES[locale]?.native ?? ariaLabel}</span>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        {SUPPORTED_LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => {
              if (locale !== code) void setLocale(code);
            }}
            className="flex items-center gap-2 cursor-pointer"
          >
            {/* Decorative: the language's native name is right beside it. */}
            <CountryFlag code={LOCALE_FLAG_COUNTRIES[code]} size="md" />
            <span className="flex-1">{LOCALE_NAMES[code].native}</span>
            {locale === code && <Check className="w-4 h-4 text-primary-accessible" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
